<template>
  <div class="customer-basket" data-cy="customer-basket">
    <div class="d-flex fl-col fl-s-b aside-wrapper">
      <div class="order-summary-wrapper d-flex fl-col fl-s-b scroll-y">
        <div class="scroll-y order-summary d-flex fl-col fl-s-b">
          <div class="upper-part-container d-flex flex-row fl-s-b">
            <div class="title d-flex fl-col fl-j-c">
              <p
                :class="{
                  'long-title': basketData.isLongTitle,
                  'max-content-width': !basketData.isLongTitle,
                }"
              >
                {{ $t("asideordersummary.title") }}
              </p>
            </div>
            <div
              class="assign-customer-btn-container d-flex flex-row fl-s-b flex-align-center"
            >
              <div class="icon-container">
                <SvgIcon
                  :iconContent="basketData.iconContent"
                  :iconAlt="basketData.iconAlt"
                  :iconStyle="basketData.iconStyle"
                />
              </div>
              <p class="max-content-width">
                {{ basketData.customerFullName }}
              </p>
            </div>
          </div>
          <div class="pss-list-container d-flex fl-col fl-s-a">
            <div
              v-for="line in basketData.lines"
              :key="line.id"
              class="customer-pss-item"
              data-cy="customer-basket-item"
              :class="line.isCancelled ? 'cancelled-pss-container' : ''"
            >
              <div
                class="customer-pss-container d-flex flex-row fl-s-b"
                data-cy="customer-basket-item-row"
              >
                <div class="ticket-name-container">
                  <p class="ticket-name" data-cy="customer-basket-item-name">
                    {{ line.name }}
                  </p>
                  <p class="price-name" v-if="line.priceName">
                    {{ line.priceName }}
                  </p>
                  <p class="timeslot" v-if="line.timeslot">
                    {{ line.timeslot }}
                  </p>
                  <p v-if="line.seatsLabel" class="seat">
                    {{ line.seatsLabel }}
                  </p>
                  <p
                    v-for="discountLine in line.discountLines"
                    :key="discountLine"
                    class="ticket-discount-info"
                  >
                    {{ discountLine }}
                  </p>
                </div>
                <div class="quantity-container">
                  <p
                    class="quantity"
                    :class="line.isCancelled ? 'erased-tickets' : ''"
                  >
                    X{{ line.quantity }}
                  </p>
                </div>
                <div class="unit-price-container">
                  <p
                    class="unit-price"
                    :class="line.isCancelled ? 'erased-tickets' : ''"
                  >
                    {{ line.unitPrice }}
                  </p>
                </div>
                <div
                  class="total-price-container"
                  :class="{ 'with-pt': line.discountedTotal }"
                  data-cy="customer-basket-item-total-price"
                >
                  <p
                    class="total-price"
                    :class="{
                      'erased-tickets':
                        line.isCancelled || line.discountedTotal,
                    }"
                  >
                    {{ line.totalPrice }}
                  </p>
                  <p class="total-price" v-if="line.discountedTotal">
                    {{ line.discountedTotal }}
                  </p>
                </div>
              </div>
              <div
                v-if="line.statusLabel"
                class="tickets-status-container"
                data-cy="customer-basket-item-status"
              >
                <p>
                  {{ line.statusLabel }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div class="order-summary">
            <!-- Discount -->
            <div
              class="pss-list-container"
              style="padding-top: 3px; padding-bottom: 3px"
            >
              <el-row
                v-if="basketData.showDiscountRow"
                class="total-container discount-container"
                justify="start"
              >
                <el-col :span="5" class="total-container">
                  {{ `${$t("button.discount")}:` }}
                </el-col>
                <el-col :span="5" class="total-container">
                  {{ basketData.currency }}
                </el-col>
                <el-col :span="11" class="d-flex flex-row fl-a-c fl-j-e">
                </el-col>
                <el-col class="total-container text-right" :span="3">
                  {{ basketData.totalDiscounts }}
                </el-col>
              </el-row>
            </div>
            <!-- Subtotal -->
            <div
              v-if="basketData.showTotals"
              class="w-full d-flex flex-1 fl-s-e fl-a-c"
            >
              <div class="total-container text-left" style="width: 33%">
                {{ `${$t("field.subtotal")}:` }}
              </div>
              <div class="total-container text-center" style="width: 33%">
                {{ basketData.currency }}
              </div>
              <div class="total-container text-right" style="width: 33%">
                {{ basketData.subtotal }}
              </div>
            </div>

            <!-- Tax -->
            <div
              v-if="basketData.showTotals"
              class="w-full d-flex flex-1 fl-s-e fl-a-c"
            >
              <div class="total-container text-left" style="width: 33%">
                {{ `${$t("field.vat")}:` }}
              </div>
              <div class="total-container text-center" style="width: 33%">
                {{ basketData.currency }}
              </div>
              <div class="total-container text-right" style="width: 33%">
                {{ basketData.totalVat }}
              </div>
            </div>

            <!-- Total -->
            <div
              v-if="basketData.showTotal"
              class="w-full d-flex flex-1 fl-s-e fl-a-c"
            >
              <div class="total-container text-left" style="width: 33%">
                {{ `${$t("field.total")}:` }}
              </div>
              <div class="total-container text-center" style="width: 33%">
                {{ basketData.currency }}
              </div>
              <div class="total-container text-right" style="width: 33%">
                {{ basketData.total }}
              </div>
            </div>

            <!-- Total - multi currencies -->
            <div
              v-for="multiCurrencyTotal in basketData.multiCurrencyTotals"
              :key="multiCurrencyTotal.label"
              class="w-full d-flex flex-1 fl-s-e fl-a-c"
            >
              <div class="total-container text-left" style="width: 33%">
                {{ `${$t("field.total")}:` }}
              </div>
              <div class="total-container text-center p-rel" style="width: 33%">
                {{ multiCurrencyTotal.label }}

                <span style="position: absolute; right: -1rem"
                  >R {{ multiCurrencyTotal.exchangeRate }}</span
                >
              </div>
              <div class="total-container text-right" style="width: 33%">
                {{ multiCurrencyTotal.amount }}
              </div>
            </div>

            <!-- Pending voucher payments -->
            <div v-if="basketData.vouchers.length" style="padding-top: 15px">
              <div
                v-for="voucher in basketData.vouchers"
                :key="voucher.id"
                class="w-full d-flex flex-1 fl-s-e fl-a-c amounts-paid"
                style="min-height: 25px"
              >
                <div class="text-left" style="width: 33%">
                  <p>{{ voucher.label }}</p>
                  <p
                    v-if="voucher.showPendingHint"
                    class="pending-payment-hint"
                  >
                    Pending Payment
                  </p>
                </div>
                <div class="text-center" style="width: 33%">
                  <p>{{ voucher.currency }}</p>
                </div>
                <div class="d-flex flex-row fl-s-b" style="width: 33%">
                  <div class="d-flex fl-col fl-j-c"></div>
                  <div>
                    <p>{{ voucher.amount }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment amounts -->
            <div v-if="basketData.payments.length" style="padding-top: 15px">
              <div
                v-for="payment in basketData.payments"
                :key="payment.id"
                class="w-full d-flex flex-1 fl-s-e fl-a-c amounts-paid"
                style="min-height: 25px"
              >
                <div class="text-left" style="width: 33%">
                  <p>
                    {{ payment.methodLabel }}
                  </p>
                </div>
                <div class="text-center" style="width: 33%">
                  <p>{{ payment.currencyLabel }}</p>
                </div>
                <div class="d-flex flex-row fl-s-b" style="width: 33%">
                  <div class="d-flex fl-col fl-j-c"></div>
                  <div>
                    <p>{{ payment.amount }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Total Paid & Total Refunded -->
            <div
              style="border-top: 2px solid white"
              v-if="basketData.showConfirmedAmounts"
            >
              <div
                class="d-flex flex-1 fl-s-e fl-a-c"
                style="margin-bottom: -5px; max-height: 50px"
              >
                <div
                  class="confirmed-amount-total text-left"
                  style="width: 33%"
                >
                  {{ $t("asideordersummary.amount-confirmed") }}
                </div>
                <div
                  class="confirmed-amount-total text-center"
                  style="width: 33%"
                >
                  {{ basketData.currency }}
                </div>
                <div
                  class="confirmed-amount-total text-right"
                  style="width: 33%"
                >
                  {{ basketData.totalConfirmedAmount }}
                </div>
              </div>

              <!-- FIS-715: the gratuity the customer added on the terminal. -->
              <div
                v-if="basketData.showTip"
                class="d-flex flex-1 fl-s-e fl-a-c"
                style="margin-bottom: -15px"
                data-cy="customer-basket-tip"
              >
                <div
                  class="confirmed-amount-total text-left"
                  style="width: 33%"
                >
                  {{ $t("field.tip") }}
                </div>
                <div
                  class="confirmed-amount-total text-center"
                  style="width: 33%"
                >
                  {{ basketData.currency }}
                </div>
                <div
                  class="confirmed-amount-total text-right"
                  style="width: 33%"
                  data-cy="customer-basket-tip-amount"
                >
                  {{ basketData.tipAmount }}
                </div>
              </div>

              <div
                v-if="basketData.showRefundedAmount"
                class="d-flex flex-1 fl-s-e fl-a-c"
                style="margin-bottom: -15px"
              >
                <div
                  class="confirmed-amount-total text-left"
                  style="width: 33%"
                >
                  {{ $t("asideordersummary.amount-refunded") }}
                </div>
                <div
                  class="confirmed-amount-total text-center"
                  style="width: 33%"
                >
                  {{ basketData.currency }}
                </div>
                <div
                  class="confirmed-amount-total text-right"
                  style="width: 33%"
                >
                  {{ basketData.refundedAmount }}
                </div>
              </div>
            </div>
          </div>
          <div class="accountings-container d-flex flex-row fl-s-b">
            <p class="title d-flex fl-col fl-j-c">
              {{ $t("asideordersummary.balance-in") }}
              {{ basketData.currency }}
            </p>
            <p class="value d-flex fl-col fl-j-c">
              {{ basketData.balance }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { CustomerBasketData } from "@/utils/vue-helpers/customer-display";

/**
 * Customer-facing mirror of AsideOrderSummary. Pure renderer: every row,
 * label and amount is precomputed in the main window (see
 * `getCustomerBasketData()`) and received over the broadcast channel — this
 * window has no order store of its own.
 */
export default defineComponent({
  name: "CustomerBasket",
  props: {
    basketData: {
      required: true,
      type: Object as PropType<CustomerBasketData>,
    },
  },
});
</script>

<style lang="scss">
// In order to make the below responsive we will need to:
// 0- Take sizes properties computed values from MAC screen & turn them into vh values to be responsive
// 1- Take 100vh from Hamza's MAC screen window.innerHeight (791px) and calculate variable avh = 791/100 = 7.91
// 2- For each row in Hamza's MAC screen, take computed following properties: height & min-height, and convert their values to be related to avh
// 3- example: .upper-container-part computed height = 45px --> newRelativeHeight = 45 / avh = 5.68 so we write in the class height:5.68vh
// 4- example: .customer-pss-container computed height 86px and min-height 80px, so we do newRelativeMinHeight = 80/avh = 10.11 so new min-height=10.11vh. No need for height here as it's not mentionned below
// 5- Width that are using percentage should not be updated
// 6- Width that are using static values should be changed to percentage
// 7- After getting done with height property we will need to do the same with paddings & margins (bottom & top)
// 8- After getting done from rows height measures, we proceed with font-size & line-height of each one and use the same logic so that font-size &line-height will be related to vh (implicitly they are related to their own parents' height)
// 9- Icons (for now only customer icon) are now using static values, they should be using percentage for both width and height to be relative to their own parents.

@import "@/styles/variables.scss";

// One knob for every text size on the customer display (L3-8402)
$customer-display-font-scale: 1.25;

@function scaled-size($size) {
  @return $size * $customer-display-font-scale;
}

.customer-basket {
  width: 35%;
  // $summary-width: 390px;

  .el-aside {
    width: unset;
  }

  .order-summary-wrapper {
    flex-grow: 3;
    width: 100%;
    // padding: 5px 15px;
    padding-top: 0.63vh; //5px;==> newPaddingTop = 5/avh = 5/7.91 = 0.63
    padding-bottom: 0.63vh; //5px;==> newPaddingBottom = 5/avh = 5/7.91 = 0.63
    border-radius: $border-radius;
    background-color: $color-dark-grey-2;
    color: white;

    .order-summary {
      padding-left: 15px;
      padding-right: 15px;
      .upper-part-container {
        height: 5.86vh; //45px;==> newHeight = 45/avh = 45/7.91 = 5.86
        min-height: 5.86vh; //45px;==> newHeight = 45/avh = 45/7.91 = 5.86

        .title {
          text-align: left;
          line-height: 3.67vh; //29px;==> lineHeight = 29/avh = 29/7.91 = 3.67
          letter-spacing: 0px;
          color: white;
          font-weight: 700;
          font-style: normal;
          font-size: 2.78vh; //22px;==> fontSize = 22/avh = 22/7.91 = 2.78
        }

        .max-content-width {
          width: max-content;
        }

        .long-title {
          font-size: 2.02vh; //16px;==> fontSize = 16/avh = 16/7.91 = 2.02
          line-height: 2.52vh; //20px;==> lineHeight = 20/avh = 20/7.91 = 2.52
        }

        .assign-customer-btn-container {
          line-height: 2.15vh; //17px;==> lineHeight = 17/avh = 17/7.91 = 2.15
          letter-spacing: 0px;
          color: white;
          font-weight: 400;
          font-style: normal;
          font-size: 1.64vh; //13px;==> fontSize = 13/avh = 13/7.91 = 1.64
          text-align: center;
          border-left: 1px solid gray;
          padding-left: 10px;

          .icon-container {
            margin-right: 10px;
          }
        }
      }

      .pss-list-container {
        padding-top: 1.26vh; //10px;==> newPaddingTop = 10/avh = 10/7.91 = 1.26
        padding-bottom: 1.26vh; //10px;==> newPaddingBottom = 10/avh = 10/7.91 = 1.26

        // Row and status label share one bordered block
        .customer-pss-item {
          border-bottom: 1px solid white;
        }

        .customer-pss-container {
          padding-top: 0.37vh; //3px;==> newPaddingTop = 3/avh = 3/7.91 = 0.37
          padding-bottom: 0.37vh; //3px;==> newPaddingTop = 3/avh = 3/7.91 = 0.37
          // min-height: 10.11vh; //80px;==> newMinHeight = 80/avh = 80/7.91 = 10.11
          // min-height: 2.52vh; //20px;==> newHeight = 20/avh = 20/7.91 = 2.52
          align-items: center;

          .ticket-name-container {
            width: 40%;
            text-align: left;

            .ticket-name {
              font-weight: 700;
              font-style: normal;
              font-size: scaled-size(1.76vh);
              line-height: scaled-size(1.76vh);
              margin-bottom: 0.63vh; //5px;==> newPaddingTop = 5/avh = 5/7.91 = 0.63
            }
            .price-name,
            .ticket-discount-info {
              font-weight: 400;
              font-style: normal;
              font-size: scaled-size(1.26vh);
              line-height: scaled-size(1.76vh);
            }
            .ticket-discount-info {
              font-weight: 600;
            }
            .timeslot {
              font-weight: 400;
              font-style: normal;
              font-size: scaled-size(1.26vh);
              line-height: scaled-size(1.76vh);
            }
            .seat {
              font-weight: 400;
              font-style: normal;
              font-size: scaled-size(1.26vh);
              line-height: scaled-size(1.76vh);
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
            }
          }

          .quantity-container {
            width: 20%;
            text-align: center;
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;

            .quantity {
              font-weight: 700;
              font-style: normal;
              font-size: scaled-size(1.51vh);
              text-align: right;
              line-height: scaled-size(1.76vh);
              letter-spacing: 0px;
              margin-right: 8px;
            }
          }

          .unit-price-container {
            width: 20%;
            text-align: center;

            .unit-price {
              font-weight: 700;
              font-style: normal;
              font-size: scaled-size(1.51vh);
              text-align: right;
              line-height: scaled-size(1.76vh);
              letter-spacing: 0px;
            }
          }
          .total-price-container.with-pt {
            padding-top: 1.2rem;
          }
          .total-price-container {
            width: 20%;
            text-align: right;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;

            .total-price {
              font-weight: 700;
              font-style: normal;
              font-size: scaled-size(1.51vh);
              text-align: right;
              line-height: scaled-size(1.76vh);
              letter-spacing: 0px;
            }
          }

          .erased-tickets {
            text-decoration: line-through;
          }
        }

        .tickets-status-container {
          font-weight: 700;
          font-style: normal;
          font-size: scaled-size(1.51vh);
          line-height: scaled-size(1.51vh);
          text-align: right;
          width: 100%;
          color: yellow;
          // Keep explicit: overrides the unscoped -15px of AsideOrderSummary.vue
          margin-top: 0;
          padding-bottom: 0.63vh; //5px;==> newPaddingTop = 5/avh = 5/7.91 = 0.63
        }

        .cancelled-pss-container {
          background-color: rgba(
            $color: $color-grid-button-purple /*$color-dark-grey-2 */,
            $alpha: 0.5
          );
        }
      }

      .total-container {
        line-height: 3.79vh; //30px;==> lineHeight = 30/avh = 30/7.91 = 3.79
        height: 3.79vh; //30px;==> newHeight = 30/avh = 30/7.91 = 3.79
        font-weight: 700;
        font-style: normal;
        font-size: scaled-size(1.76vh);
        // padding-left: 1%;
      }

      .tax-container {
        border-bottom: 1px solid white;
      }

      .discount-container {
        font-size: scaled-size(1.51vh);
        line-height: 3.79vh; //30px;==> lineHeight = 30/avh = 30/7.91 = 3.79
        height: 3.79vh; //30px;==> newHeight = 30/avh = 30/7.91 = 3.79
      }

      .confirmed-amount-container {
        border-bottom: 1px solid white;
        border-top: 1px solid white;

        .confirmed-amount {
          height: 5.05vh; //40px;==> newHeight = 40/avh = 40/7.91 = 5.05
          font-weight: 700;
          font-style: normal;
          font-size: 1.76vh; //14px;==> fontSize = 14/avh = 14/7.91 = 1.76
          line-height: 1.76vh; //14px;==> lineHeight = 14/avh = 14/7.91 = 1.76
          margin-bottom: 0.63vh; //5px;==> newMarginBottom = 5/avh = 5/7.91 = 0.63
        }
      }

      .confirmed-amount-total {
        font-weight: 700;
        font-style: normal;
        font-size: scaled-size(1.76vh);
        // text-align: right;
        // Row height from min-height so a wrapped label (DE) keeps its lines together
        min-height: calc(6.32 * 1.76vh);
        line-height: scaled-size(1.76vh);
        display: flex;
        flex-direction: column;
        justify-content: center;
        // height: 50px;
        letter-spacing: 0px;
      }
    }

    .accountings-container {
      height: 5.68vh; //45px;==> newHeight = 45/avh = 45/7.91 = 5.68
      border-top: 2px solid white;
      padding-left: 15px;
      padding-right: 15px;

      .title {
        text-align: left;
      }
      .value {
        text-align: right;
      }
      .title,
      .value {
        font-weight: 700;
        font-style: normal;
        font-size: scaled-size(2.02vh);
        text-align: left;
        line-height: scaled-size(2.52vh);
        letter-spacing: 0px;
      }
    }
  }

  .aside-wrapper {
    width: 100%;
    height: 100vh;
    min-height: 100%;
  }

  .amounts-paid {
    font-weight: 800;
    font-style: normal;
    font-size: scaled-size(1.76vh);
    line-height: scaled-size(2.52vh);
    letter-spacing: 0px;
  }

  .pending-payment-hint {
    font-weight: 400;
    font-size: 1.39vh; //11px;==> fontSize = 11/avh = 11/7.91 = 1.39
    line-height: 1.76vh; //14px;==> lineHeight = 14/avh = 14/7.91 = 1.76
    color: #959595;
  }
}
</style>
